import { useState } from 'react'
import { BiSolidStar, BiStar } from 'react-icons/bi'
import { FormControl, HStack, Icon, Textarea, VStack } from '@chakra-ui/react'
import { Button, FormLabel, IconButton } from '@opengovsg/design-system-react'

import { CSAT_COMMENT_MAX_LEN } from '~shared/constants'

const getCommentLabel = (score: number): string => {
  if (score <= 2) return 'What went wrong?'
  if (score === 3) return 'What could we improve?'
  return 'What did you enjoy most?'
}

export interface CsatFormProps {
  onSubmit: (values: { score: number; comment?: string }) => void
  isSubmitting: boolean
}

/**
 * Star rating + adaptive comment field, shared by the respondent widget
 * and the admin modal. The comment box only appears once a score is picked.
 */
export const CsatForm = ({ onSubmit, isSubmitting }: CsatFormProps) => {
  const [score, setScore] = useState<number | null>(null)
  const [comment, setComment] = useState('')

  return (
    <VStack alignItems="start" spacing={4} w="full">
      <HStack spacing={1} role="radiogroup" aria-label="Rating out of 5 stars">
        {[1, 2, 3, 4, 5].map((star) => (
          <IconButton
            key={star}
            variant="clear"
            aria-label={`${star} star${star === 1 ? '' : 's'}`}
            role="radio"
            aria-checked={score === star}
            onClick={() => setScore(star)}
            icon={
              <Icon
                as={score !== null && star <= score ? BiSolidStar : BiStar}
                fontSize="1.75rem"
                color={
                  score !== null && star <= score
                    ? 'interaction.warning.default'
                    : 'base.content.medium'
                }
              />
            }
          />
        ))}
      </HStack>
      {score !== null && (
        <>
          <FormControl>
            <FormLabel>{getCommentLabel(score)}</FormLabel>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={CSAT_COMMENT_MAX_LEN}
              placeholder="Optional"
            />
          </FormControl>
          <Button
            isLoading={isSubmitting}
            onClick={() =>
              onSubmit({ score, comment: comment.trim() || undefined })
            }
          >
            Submit feedback
          </Button>
        </>
      )}
    </VStack>
  )
}
